import { useCallback, useEffect, useMemo, useState } from "react";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { AdminHeader } from "@trillion/ui/components/admin/admin-header";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { DataTableWrapper } from "@trillion/ui/components/admin/data-table-wrapper";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@trillion/ui/components/ui/table";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/admin-api";
import { getOAuthClientColumns } from "@/components/oauth-clients/oauth-client-columns";
import { OAuthClientDialog } from "@/components/oauth-clients/oauth-client-dialog";
import type { OAuthClient } from "@/types";

export function ApplicationsPage() {
  const [data, setData] = useState<OAuthClient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OAuthClient | undefined>();

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setData(await adminApi.oauthClients.list());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load applications");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = useCallback(() => {
    setEditing(undefined);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((c: OAuthClient) => {
    setEditing(c);
    setDialogOpen(true);
  }, []);

  const columns = useMemo(() => getOAuthClientColumns({ onEdit: openEdit }), [openEdit]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col">
      <AdminHeader
        title="Applications"
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Applications" }]}
      />
      <div className="flex flex-col gap-6 p-6">
        <PageHeader
          title="Applications"
          description="OAuth clients — public (PKCE) and confidential. Secrets are hashed at rest and masked here."
          actions={
            <Button onClick={openCreate}>
              <Plus className="size-4" />
              Create client
            </Button>
          }
        />

        <DataTableWrapper
          title="OAuth Clients"
          description="Applications registered with Passport"
          isLoading={isLoading}
        >
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-muted-foreground"
                  >
                    No applications yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DataTableWrapper>
      </div>

      <OAuthClientDialog
        editing={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={load}
      />
    </div>
  );
}
